import SimpleBar from 'simplebar-react';
import classNames from "classnames";
import { useContext, useMemo, useState } from 'react';

import { EtfLabel } from 'shared/ui-kit';
import { AdsComponent } from 'shared/components/ads';
import { decodeSiteResolutionValue, SiteResolutionContext } from 'shared/mechanics/site-resolution';

import { EtfFactorAnalysisTop } from './top';
import { EtfFactorAnalysisFactor } from './factor';
import { EtfFactorAnalysisCorrelation } from './correlation';
import { EtfFactorAnalysisComparison } from './comparison';

import { FactorAnalsysisBackend } from "api/analysis";
import { IEtfBackend } from "api/etf";

import { FactorViewModel } from '../model';

import styles from "./content.module.scss";
import { checkUserClaims } from 'shared/auth';
import { UserClaims } from 'shared/mechanics/auth';

enum CurrencyType {
	RUB = "RUB",
	USD = "USD",
	AUTO = "AUTO"
}
const CurrencyTypeNames = {
	[CurrencyType.AUTO]: 'AUTO',
	[CurrencyType.RUB]: 'RUB',
	[CurrencyType.USD]: 'USD'
}

export interface EtfFactorAnalysisPageContentProps {
	etf: IEtfBackend;
	factorAnalysisRub: FactorAnalsysisBackend | null;
	factorAnalysisUsd: FactorAnalsysisBackend | null;
}
export const EtfFactorAnalysisPageContent = (props: EtfFactorAnalysisPageContentProps) => {
	const [currencyType, setCurrencyType] = useState(CurrencyType.AUTO);

	const factorAnalysis = decodeFactorAnalysis(currencyType, props);
	const availableCurrencyTypes = calculateAvailableCurrencyTypes(props);

	const factors = useMemo<FactorViewModel[]>(
		() => {
			return factorAnalysis.movement.entries
				.map(movement => {
					const up = factorAnalysis.up.entries.find(x => x.compareTicker == movement.compareTicker);
					const down = factorAnalysis.down.entries.find(x => x.compareTicker == movement.compareTicker);

					return {
						ticker: movement.compareTicker,
						description: movement.compareDescription,

						beta: movement.beta,
						correlation: movement.correlation,
						overYield1Y: movement.overYield1Y,

						up: {
							count: factorAnalysis.up.count,
							otherUpCount: up?.upCounts || 0,
							otherDownCount: up?.downCounts || 0
						},
						down: {
							count: factorAnalysis.down.count,
							otherUpCount: down?.upCounts || 0,
							otherDownCount: down?.downCounts || 0
						}
					}
				})
				.sort((first, second) => Math.abs(second.correlation) - Math.abs(first.correlation));
		},
		[]);

	const siteResolution = useContext(SiteResolutionContext);
	const ads2Key = decodeSiteResolutionValue(siteResolution, 'InfoDetailedHorizontal2:desktop', 'InfoDetailedHorizontal2:mobile');

	function onCurrencyTypeChange(currencyType: CurrencyType) {
		checkUserClaims(UserClaims.HAVE_SUBSCRIPTION, "Валютные метрики в подписке", () => {
			setCurrencyType(currencyType);
		});
	}

	return (
		<div className={classNames("flex-size-fill", "flex-container-vertical", styles.container)}>
			<div className={classNames("flex-size-least", "flex-container-horizontal", styles.header)}>
				<h1 className={"flex-size-fill"}>Риски и стимулы <EtfLabel ticker={props.etf.ticker} /></h1>
				<div>
					<span className={styles.currency_label}>Валюта:</span>
					<select className={styles.currency_select} value={currencyType} onChange={e => onCurrencyTypeChange(e.currentTarget.value as CurrencyType)}>
						{
							availableCurrencyTypes.map(ct => <option value={ct}>{CurrencyTypeNames[ct]}</option>)
						}
					</select>
				</div>
			</div>
			<div className={classNames("flex-size-fill", styles.content)}>
				<SimpleBar style={{ width: '100%', height: '100%' }}>
					<EtfFactorAnalysisTop ticker={props.etf.ticker} up={factorAnalysis.up} down={factorAnalysis.down} className={styles.item} />
					<div className={styles.item}>
						<AdsComponent id="InfoDetailedHorizontal1" isVertical={false} />
					</div>
					<EtfFactorAnalysisCorrelation ticker={props.etf.ticker} movement={factorAnalysis.movement} className={styles.item} />
					<EtfFactorAnalysisComparison ticker={props.etf.ticker} className={styles.item} />
					<div className={styles.item}>
						<AdsComponent id={ads2Key} isVertical={false} />
					</div>
					{
						factors.map((factor, index) => <EtfFactorAnalysisFactor key={factor.ticker} ticker={props.etf.ticker} factor={factor} className={styles.item} isExpandedByDefault={index === 0} />)
					}
				</SimpleBar>
			</div>
		</div>
	);
}

function decodeFactorAnalysis(currencyType: CurrencyType, props: EtfFactorAnalysisPageContentProps): FactorAnalsysisBackend {
	if (currencyType === CurrencyType.RUB)
		return props.factorAnalysisRub!;
	if (currencyType === CurrencyType.USD)
		return props.factorAnalysisUsd!;

	if (props.etf.tags.investmentPolicy.assetCurrency === "RUB")
		return (props.factorAnalysisRub || props.factorAnalysisUsd)!;
	else
		return (props.factorAnalysisUsd || props.factorAnalysisRub)!;
}
function calculateAvailableCurrencyTypes(props: EtfFactorAnalysisPageContentProps) {
	if (props.factorAnalysisRub !== null && props.factorAnalysisUsd !== null)
		return [CurrencyType.AUTO, CurrencyType.RUB, CurrencyType.USD];
	if (props.factorAnalysisRub !== null)
		return [CurrencyType.RUB];

	if (props.etf.tags.investmentPolicy.assetCurrency === "RUB")
		return [CurrencyType.RUB];

	return [CurrencyType.USD];
}
